import { useEffect, useState } from 'react';
import { navigate } from '@/hooks/useRoute';
import { createRoom, CreateRoomError } from '@/protocol/httpClient';

/**
 * 入口页（PRD §B.2）
 *
 * - 4 位数字回显框
 * - 九宫格 0-9 + 退格 + 清空 + 加入
 * - 「创建新房间」按钮 → POST /room → 拿到 roomCode → 跳 /r/XXXX
 */
export function RoomEntryView(): JSX.Element {
  const [input, setInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 输入时清错误
  useEffect(() => {
    if (input.length > 0 && error) setError(null);
  }, [input, error]);

  const onPress = (d: string): void => {
    setInput((prev) => (prev.length >= 4 ? prev : prev + d));
  };
  const onBackspace = (): void => setInput((p) => p.slice(0, -1));
  const onClear = (): void => setInput('');
  const onJoin = (): void => {
    if (input.length !== 4) return;
    navigate(`/r/${input}`);
  };
  const onCreate = async (): Promise<void> => {
    if (creating) return;
    setCreating(true);
    setError(null);
    try {
      const { roomCode } = await createRoom();
      navigate(`/r/${roomCode}`);
    } catch (e) {
      if (e instanceof CreateRoomError) {
        if (e.code === 'ROOM_POOL_EXHAUSTED') {
          setError('房间池已满，请稍后重试');
        } else if (e.code === 'NETWORK_ERROR') {
          setError('网络错误，请检查连接');
        } else {
          setError(`创建房间失败：${e.message}`);
        }
      } else {
        setError('创建房间失败，请重试');
      }
    } finally {
      setCreating(false);
    }
  };

  const digits = Array.from({ length: 4 }, (_, i) => input[i] ?? '_');
  const joinable = input.length === 4 && !creating;

  return (
    <main
      data-testid="room-entry-view"
      className="mx-auto flex min-h-screen max-w-md flex-col items-center px-6 pb-10 pt-12"
    >
      <h1 className="mb-8 text-3xl font-bold tracking-wider text-pig-700">Pigwords</h1>

      {/* 4 位回显 */}
      <div
        data-testid="room-input-display"
        aria-label="房间号输入"
        className="mb-6 flex gap-3 font-mono text-5xl tabular-nums text-pig-800"
      >
        {digits.map((d, i) => (
          <span
            key={i}
            data-testid={`digit-${i}`}
            className={
              d === '_'
                ? 'inline-block w-12 border-b-4 border-pig-300 text-center text-pig-300'
                : 'inline-block w-12 border-b-4 border-pig-600 text-center'
            }
          >
            {d}
          </span>
        ))}
      </div>

      {/* 九宫格 */}
      <div className="grid w-full grid-cols-3 gap-3" role="group" aria-label="数字键盘">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            type="button"
            data-testid={`key-${d}`}
            onClick={() => onPress(d)}
            className="h-16 rounded-xl bg-pig-100 text-2xl font-semibold text-pig-800 active:bg-pig-200"
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          data-testid="key-backspace"
          aria-label="退格"
          onClick={onBackspace}
          className="h-16 rounded-xl bg-pig-100 text-xl text-pig-700 active:bg-pig-200"
        >
          ⌫
        </button>
        <button
          type="button"
          data-testid="key-0"
          onClick={() => onPress('0')}
          className="h-16 rounded-xl bg-pig-100 text-2xl font-semibold text-pig-800 active:bg-pig-200"
        >
          0
        </button>
        <button
          type="button"
          data-testid="key-join"
          aria-label="加入房间"
          onClick={onJoin}
          disabled={!joinable}
          className="h-16 rounded-xl bg-pig-600 text-xl font-semibold text-white disabled:bg-pig-300 disabled:text-pig-100"
        >
          →
        </button>
      </div>

      {/* 清空 */}
      <button
        type="button"
        data-testid="key-clear"
        onClick={onClear}
        disabled={input.length === 0}
        className="mt-3 text-sm text-pig-500 underline disabled:text-pig-300 disabled:no-underline"
      >
        清空
      </button>

      {/* 分隔 */}
      <div className="my-6 flex w-full items-center gap-3 text-pig-400">
        <div className="h-px flex-1 bg-pig-200" />
        <span className="text-sm">或</span>
        <div className="h-px flex-1 bg-pig-200" />
      </div>

      {/* 创建房间 */}
      <button
        type="button"
        data-testid="btn-create-room"
        onClick={onCreate}
        disabled={creating}
        className="h-14 w-full rounded-xl bg-pig-700 text-lg font-semibold text-white disabled:bg-pig-400"
      >
        {creating ? '创建中…' : '创建新房间'}
      </button>

      {/* 错误提示 */}
      {error && (
        <div
          data-testid="room-entry-error"
          role="alert"
          className="mt-4 w-full rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}
    </main>
  );
}
